// @flow

// live

import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { ContainerFields, renderFormConfig } from './utils'

const getLookupTable = (signal, lookup_key) => fetch(`/rest/lookup/${lookup_key}`, { signal }).then(r => {
    if (r.ok) {
        return r.json()
    } else {
        return Promise.reject(`Could not find lookup "${lookup_key}"`)
    }
})

type StateProps = {
    drillingSubPeerGroup: string
    drillingPeerGroup: string
    fieldName: string
    wellType: string
    wellProfile: string
    rigManufacturer: string
    drillingOperationManagement: string
    completionType: string
    isMaximumReservoirContact: boolean
    isPlannedPilotHole: boolean
    isLowerCompletionWells: boolean
    isIntegratedDrillingServices: boolean
    usesScrapperWhileDrilling: boolean
    usesTrippleCatwalk: boolean
    isLinerLess: boolean
    spudDate: number
}

type FormConfigRowProps = {
    componentType: string
    property: string
    label: string
    placeHolder: string
    options: Object[]
}

type DataProps = {
    drillingSubPeerGroup: string
    drillingPeerGroup: string
    fieldName: string
    wellType: string
    wellProfile: string
    rigManufacturer: string
    drillingOperationManagement: string
    completionType: string
    isMaximumReservoirContact: boolean
    isPlannedPilotHole: boolean
    isLowerCompletionWells: boolean
    isIntegratedDrillingServices: boolean
    usesScrapperWhileDrilling: boolean
    usesTrippleCatwalk: boolean
    isLinerLess: boolean
    spudDate: number
}

type Props = {
    data: DataProps
    setData: Function
    fieldProps: (string) => Object
    inputProps: (string) => Object
    label: (string) => Object
    text: (Object) => Object
    readOnly: boolean
}

export function ExtraMetadata({ data, fieldProps, label, setData, inputProps, readOnly }: Props) {
    let [state, setState] = useState<StateProps>({
        drillingSubPeerGroup: data?.drillingSubPeerGroup,
        drillingPeerGroup: data?.drillingPeerGroup,
        fieldName: data?.fieldName,
        wellType: data?.wellType,
        wellProfile: data?.wellProfile,
        rigManufacturer: data?.rigManufacturer,
        drillingOperationManagement: data?.drillingOperationManagement,
        completionType: data?.completionType,
        isMaximumReservoirContact: !!data?.isMaximumReservoirContact,
        isPlannedPilotHole: !!data?.isPlannedPilotHole,
        isLowerCompletionWells: !!data?.isLowerCompletionWells,
        isIntegratedDrillingServices: !!data?.isIntegratedDrillingServices,
        usesScrapperWhileDrilling: !!data?.usesScrapperWhileDrilling,
        usesTrippleCatwalk: !!data?.usesTrippleCatwalk,
        isLinerLess: !!data?.isLinerLess,
        spudDate: data?.spudDate
    })
    let [optionsState, setOptionsState] = useState<Object>({})
    let [formRowsConfig, setFormRowsConfig] = useState<FormConfigRowProps[][]>([[]])

    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal

        const lookups = {
            options_field: [],
            options_dpg: [],
            options_dspg: [],
            options_dom: [],
            options_well_type: [],
            options_well_profile: [],
            options_rig_manufacturer: [],
            options_completion_type: [],
            options_jobtype_description: [],
        }

        const promises = Object.keys(lookups).map(key => getLookupTable(signal, key).catch((error) => console.error(error)))

        Promise.all(promises).then((results) => {
            results.map(lookup => {
                if (lookup && lookup.id)
                    lookups[lookup.id] = lookup.entries.map(({ key }) => ({ value: key, label: key }))
            })

            setOptionsState(lookups)
        })
    }, [])

    useEffect(() => {
        const config: FormConfigRowProps[][] = [
            [
                { componentType: 'date-picker', property: 'spudDate', label: 'Spud Date', placeHolder: null, options: null }
            ],
            [
                { componentType: 'select', property: 'fieldName', label: 'Field Name', placeHolder: 'Select a field', options: optionsState['options_field'] },
                { componentType: 'select', property: 'drillingPeerGroup', label: 'DPG', placeHolder: 'Select a DPG', options: optionsState['options_dpg'] },
                { componentType: 'select', property: 'drillingSubPeerGroup', label: 'DSPG', placeHolder: 'Select a DSPG', options: optionsState['options_dspg'] },
                { componentType: 'select', property: 'drillingOperationManagement', label: 'DOM', placeHolder: 'Select a DOM', options: optionsState['options_dom'] },
            ],
            [
                { componentType: 'select', property: 'wellType', label: 'Well Type', placeHolder: 'Select a Well Type', options: optionsState['options_well_type'] },
                { componentType: 'select', property: 'wellProfile', label: 'Well Profile', placeHolder: 'Select a Well Profile', options: optionsState['options_well_profile'] },
                { componentType: 'select', property: 'rigManufacturer', label: 'Rig Manufacturer', placeHolder: 'Select a Rig Manufacturer', options: optionsState['options_rig_manufacturer'] },
                { componentType: 'select', property: 'completionType', label: 'Completion Type', placeHolder: 'Select a Completion Type', options: optionsState['options_completion_type'] },
            ],

            [
                { componentType: 'switch', property: 'isMaximumReservoirContact', label: 'Maximum Reservoir Contact', placeHolder: null, options: null },
                { componentType: 'switch', property: 'isPlannedPilotHole', label: 'Planned Pilot Hole', placeHolder: null, options: null },
                { componentType: 'switch', property: 'isLowerCompletionWells', label: 'Lower Completion Wells', placeHolder: null, options: null },
                { componentType: 'switch', property: 'isIntegratedDrillingServices', label: 'IDS ADNOC', placeHolder: null, options: null },
                { componentType: 'switch', property: 'usesScrapperWhileDrilling', label: 'Scrapper While Drilling', placeHolder: null, options: null },
                { componentType: 'switch', property: 'usesTrippleCatwalk', label: 'Tripple Catwalk', placeHolder: null, options: null },
                { componentType: 'switch', property: 'isLinerLess', label: 'Liner Less', placeHolder: null, options: null },
            ]

        ]

        setFormRowsConfig(config)

    }, [JSON.stringify(optionsState), JSON.stringify(state)])

    return (
        <ContainerFields>
            {renderFormConfig(data, fieldProps, label, setData, inputProps, readOnly, formRowsConfig, state, setState)}
        </ContainerFields>
    )
}

